import { computed, ref } from 'vue'

/**
 * PWA state shared across the app: service worker registration, "new version"
 * detection, the install prompt and online/offline status.
 */
const SW_URL = '/sw.js'
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000

export const isOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
export const needRefresh = ref(false)
export const installPromptEvent = ref(null)
export const isInstalled = ref(false)
export const showIosInstallHelp = ref(false)

export const isIos = typeof navigator !== 'undefined'
  && (/iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))

export const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true

// iOS has no install prompt event: users add the app via Share -> Add to Home Screen.
export const canInstall = computed(() => !isInstalled.value && (!!installPromptEvent.value || isIos))

let waitingWorker = null
let reloading = false

const trackWaiting = registration => {
  const markWaiting = worker => {
    // Only an update if a worker already controls the page (not the first install).
    if (worker && navigator.serviceWorker.controller) {
      waitingWorker = worker
      needRefresh.value = true
    }
  }

  if (registration.waiting)
    markWaiting(registration.waiting)

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing
    if (!installing)
      return

    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed')
        markWaiting(installing)
    })
  })
}

const registerServiceWorker = async () => {
  try {
    const registration = await navigator.serviceWorker.register(SW_URL, { scope: '/' })

    trackWaiting(registration)
    setInterval(() => registration.update().catch(() => {}), UPDATE_CHECK_INTERVAL)
  }
  catch (error) {
    console.warn('Service worker registration failed:', error)
  }
}

/** Activate the waiting service worker; the page reloads once it takes control. */
export const applyUpdate = () => {
  needRefresh.value = false
  if (waitingWorker)
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
  else
    window.location.reload()
}

/** Show the native install dialog (or iOS instructions). Resolves to true if the user accepted. */
export const promptInstall = async () => {
  const event = installPromptEvent.value
  if (!event) {
    if (isIos && !isInstalled.value)
      showIosInstallHelp.value = true

    return false
  }

  installPromptEvent.value = null
  event.prompt()

  const { outcome } = await event.userChoice

  return outcome === 'accepted'
}

export const initPwa = () => {
  if (typeof window === 'undefined')
    return

  isInstalled.value = isStandalone()

  window.addEventListener('online', () => { isOnline.value = true })
  window.addEventListener('offline', () => { isOnline.value = false })

  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault()
    installPromptEvent.value = event
  })

  window.addEventListener('appinstalled', () => {
    installPromptEvent.value = null
    isInstalled.value = true
  })

  if (!('serviceWorker' in navigator) || import.meta.env.DEV)
    return

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Reload only for updates the user accepted, not for the first install taking control.
    if (reloading || !waitingWorker)
      return
    reloading = true
    window.location.reload()
  })

  if (document.readyState === 'complete')
    registerServiceWorker()
  else
    window.addEventListener('load', registerServiceWorker, { once: true })
}
